import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { PageInfoService } from '../layouts/page-info.service';
import { LoginModel } from './login-model';
import { environment } from '../../environments/environment';
import { Validate } from '../global/validate';
import { AuthenticationService } from '../authentication/authentication.service';
import { VersionService } from '../version-service';

/**
 * This component serves as the login page for the application.
 */
@Component({
	selector: 'cc-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {

// #region [ Fields ]

	/*
		All of the following fields are used for validation.
		The Show[x]Validation fields are used to turn the display of the validators on and off.
		The [x]ValidationCallback functions are callbacks so that the input-feedback components can evaluate the validity of the model.
	*/
	public ShowEmailValidation: boolean;

	public EmailValidationCallback: Function = null;

	public ShowPasswordValidation: boolean;

	public PasswordValidationCallback: Function = null;

	/** The model used for the credentials. */
	public Model: LoginModel = new LoginModel();

	/** A callback function to pass to the ccEnterKeyDown directive for the form. */
	public OnFormKeyDownCallback: Function = null;

	/** A model indicating if the invalid credentials panel should be shown. */
	public ShowInvalidCredentialsMessage: boolean = false;

// #region [ Constructor ]

	constructor(
		private _PageInfoService: PageInfoService,
		private _Router: Router,
		private _AuthenticationService: AuthenticationService,
		private _VersionService: VersionService
	) { }

// #region [ Properties ]

	public get VersionNumber(): string {
		return environment.Version;
	}

// #region [ Implemented Methods ]

	/**
	 * Component initialization.
	 */
	ngOnInit() {
		this._PageInfoService.PageTitle = "Crown Connect";
		this._PageInfoService.PageDescription = "";
		this._PageInfoService.ShowHeaderHelpPanel = this._PageInfoService.ShowHeaderUserPanel = false;

		// Bind the function callbacks to the methods in this object to scope the functions correctly for future evaluation.
		this.OnFormKeyDownCallback = this.Login.bind(this);
		this.EmailValidationCallback = this.EmailValidation.bind(this);
		this.PasswordValidationCallback = this.PasswordValidation.bind(this);

		// Redirect the user to the home page if they're already logged in.
		this.GetIsAuthenticated()
			.then((bolIsAuthenticated: boolean) => {
				if (bolIsAuthenticated)
					this._Router.navigate(["/home/"]);
			});
	}

// #region [ Event Handlers ]

	/**
	 * Event handler for the login button click.
	 */
	public OnLoginButtonClick(): void {
		this.Login();
	}

// #region [ Public Methods ]

	/**
	 * Validates the username field.
	 */
	public EmailValidation(): boolean {
		return (this.Model.Email != null && Validate.ValidateEmail(this.Model.Email));
	}

	/**
	 * Validates the password field.
	 */
	public PasswordValidation(): boolean {
		return (this.Model.Password != null && this.Model.Password != "");
	}

// #region [ Private Methods ]

	/**
	 * Uses a promise to determine if the user is currently authenticated.
	 *
	 * @returns A promise to get the authentication state.
	 */
	private GetIsAuthenticated(): Promise<boolean> {
		return new Promise<boolean>((objResolve: Function, objReject: Function) => {
			this._AuthenticationService.LoadUserClaims()
				.then(() => objResolve(this._AuthenticationService.Authenticated))
				.catch(() => objReject());
		});
	}

	/**
	 * Validates the login form.
	 */
	private ValidateUserInput(): boolean {
		this.ShowEmailValidation = this.ShowPasswordValidation = true;
		return (this.EmailValidation() && this.PasswordValidation());
	}

	/**
	 * Attempts to authenticate the user with the supplied credentials.
	 * If the user authenticates properly,
	 */
	private Login(): void {
		if (this.ValidateUserInput()) {
			this._AuthenticationService.Login(this.Model.Email, this.Model.Password)
				.then(() => {
					if (this._AuthenticationService.Authenticated) {
						this._Router.navigate(["/home/"]);
						this._VersionService.ReloadIfClientUpdateIsAvailable();
					}
				})
				.catch(() => {
					this.ShowInvalidCredentialsMessage = true;
				});
		}
	}

}